import React from "react";
import {Tooltip} from "@mui/material";
import {Tag} from "~/components/tag/Tag.tsx";
import type {ContractDocument} from "../contractExplorerTypes.ts";
import {formatDate, ISSUE_LABEL, SIGNATURE_LABEL, typeLabel} from "../contractExplorerFormat.ts";
import {fetchContractDocumentFile} from "../useContractExplorerQueries.ts";
import {CollapsibleSection} from "./CollapsibleSection.tsx";

type Props = {
    bagId: number;
    contractId: number;
    documents: ContractDocument[];
    expanded: boolean;
    onChange: (expanded: boolean) => void;
};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * The document's name; a button that opens it in a new tab when it has been copied for viewing.
 * The file is streamed through the API with the user's token (the bucket is private), so the tab
 * is opened on the click itself, before the request, for the popup blocker to let it through.
 */
const DocumentName: React.FC<{bagId: number; contractId: number; document: ContractDocument}> = (
    {bagId, contractId, document: d},
) => {
    const [failed, setFailed] = React.useState(false);
    const name = d.file_name || d.document_id;
    const open = async () => {
        setFailed(false);
        const tab = window.open('', '_blank');
        try {
            const url = URL.createObjectURL(await fetchContractDocumentFile(bagId, contractId, d.document_id));
            if (tab) {
                tab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
            // The tab has loaded the document by then; release the memory.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            tab?.close();
            setFailed(true);
        }
    };
    if (!d.has_file) return <span className="ci-ref-f" title={d.uri}>{name}</span>;
    return (
        <span>
            <button type="button" className="ci-ref-f ci-link" title="Open the document in a new tab"
                    onClick={() => void open()}>{name}</button>
            {failed && <span className="ci-ref-m"> · could not open it</span>}
        </span>
    );
};

/** What one document's own header says about it: signature, its date, and the period it states. */
function documentFacts(header: Record<string, unknown> | null): string[] {
    if (!header) return [];
    const signed = text(header.signed_date), dated = text(header.document_date);
    const effective = text(header.effective_date), expires = text(header.expiration_date);
    return [
        SIGNATURE_LABEL[text(header.signature_status)] ?? '',
        signed ? `signed ${formatDate(signed)}` : dated ? `dated ${formatDate(dated)}` : '',
        effective || expires ? `${effective ? `effective ${formatDate(effective)}` : ''}${effective && expires ? ' → ' : ''}`
            + `${expires ? `expires ${formatDate(expires)}` : ''}` : '',
    ].filter(Boolean);
}

/** The bundle's documents, numbered: the `[n]` every extracted value cites, with what each one says of itself. */
export const SourcesSection: React.FC<Props> = ({bagId, contractId, documents, expanded, onChange}) => (
    <CollapsibleSection title={`Documents (${documents.length})`} expanded={expanded} onChange={onChange}
                        sub="The documents this bundle is built from. Each value below cites one as [n].">
        <ol className="ci-card ci-refs-list">
            {documents.map(d => {
                // Truncated is about the extraction's answer, not the file, so it is not listed.
                const issues = Array.isArray(d.header?.document_issues)
                    ? d.header.document_issues.map(String).filter(issue => issue !== 'truncated') : [];
                const details = text(d.header?.document_issues_details);
                return (
                    <li key={d.document_id} id={`ci-ref-${d.position}`} className="ci-ref">
                        <span className="ci-ref-n">[{d.position}]</span>
                        <span className="ci-ref-b">
                            <DocumentName bagId={bagId} contractId={contractId} document={d}/>
                            <span className="ci-ref-m">
                                {typeLabel(String(d.header?.document_type ?? '')) !== '—'
                                    ? typeLabel(String(d.header?.document_type)) : typeLabel(d.role)}
                                {d.role === 'opens' ? ' · opens a period' : d.role === 'reference' ? ' · reference' : ''}
                                {d.shared_from ? ` · framework from bundle ${d.shared_from}` : ''}
                                {documentFacts(d.header).map(fact => ` · ${fact}`).join('')}
                            </span>
                            {issues.length > 0 && (
                                <Tooltip arrow title={details || 'Problems the extraction found in this file'}>
                                    <span className="ci-line-tags">
                                        {issues.map(issue => (
                                            <Tag key={issue} label={ISSUE_LABEL[issue] ?? typeLabel(issue)} color="warning"
                                                 variant="outlined"/>
                                        ))}
                                    </span>
                                </Tooltip>
                            )}
                        </span>
                        <span className="ci-ref-p" title={d.relative_path}>{d.relative_path}</span>
                    </li>
                );
            })}
        </ol>
    </CollapsibleSection>
);
